import { Account } from "@common/types/domains/accounting/account";
import { Entity } from "@common/types/entity";
import { currencyFormatter } from "@common/helpers/currency";
import { ColumnDef } from "@components/spreadsheet/types";
import {
    AccountSearchCell,
    BaseCellProps,
    CellType,
} from "@components/spreadsheet/cells";
import { TransactionSplitClassCell } from "@components/transactions/TransactionSplitV2/TransactionSplitClassCell";
import { TransactionSplitCounterpartyCell } from "@components/transactions/TransactionSplitV2/TransactionSplitCounterpartyCell";
import { getEntityScopedAccounts } from "./eliminatingEntryGridData";
import { EliminatingEntryColumnMeta } from "./eliminatingEntrySpreadsheetColumns";

interface BuildColumnsParams {
    visibleColumns: readonly EliminatingEntryColumnMeta[];
    entities: Entity[];
    accountsByEntityId: Record<number, Account[]>;
    counterpartiesMap: Map<string, string>;
    debitTotal: number;
    creditTotal: number;
}

function buildColumn({
    column,
    entities,
    accountsByEntityId,
    counterpartiesMap,
    debitTotal,
    creditTotal,
}: Omit<BuildColumnsParams, "visibleColumns"> & {
    column: EliminatingEntryColumnMeta;
}): ColumnDef {
    switch (column.field) {
        case "":
            return {
                field: column.field,
                label: column.label,
                cellType: CellType.ROW_NUMBER,
                width: 40,
            };
        case "entityId":
            return {
                field: column.field,
                label: column.label,
                cellType: CellType.ENTITY,
                entities,
            };
        case "accountId":
            return {
                field: column.field,
                label: column.label,
                cellType: CellType.ACCOUNT_SEARCH,
                aggregation: () => "Total:",
                cellFn: (props) => (
                    <AccountSearchCell
                        {...props}
                        value={
                            typeof props.value === "string" ? props.value : null
                        }
                        accounts={getEntityScopedAccounts(
                            typeof props.row.entityId === "number"
                                ? props.row.entityId
                                : null,
                            accountsByEntityId,
                        )}
                    />
                ),
            };
        case "counterpartyId":
            return {
                field: column.field,
                label: column.label,
                cellType: CellType.COUNTERPARTY,
                counterpartiesMap,
                cellFn: (props) => (
                    <TransactionSplitCounterpartyCell
                        {...props}
                        value={
                            typeof props.value === "string" ||
                            props.value === null
                                ? props.value
                                : null
                        }
                        counterpartiesMap={counterpartiesMap}
                    />
                ),
            };
        case "classIds":
            return {
                field: column.field,
                label: column.label,
                cellType: CellType.CLASS,
                cellFn: (props: BaseCellProps) => (
                    <TransactionSplitClassCell
                        {...props}
                        value={
                            Array.isArray(props.value) || props.value === null
                                ? props.value
                                : null
                        }
                    />
                ),
            };
        case "debitAmount":
            return {
                field: column.field,
                label: column.label,
                cellType: CellType.MATH,
                isMonetary: true,
                aggregation: () => currencyFormatter.format(debitTotal),
            };
        case "creditAmount":
            return {
                field: column.field,
                label: column.label,
                cellType: CellType.MATH,
                isMonetary: true,
                aggregation: () => currencyFormatter.format(creditTotal),
            };
        case "excludeFrom1099":
            return {
                field: column.field,
                label: column.label,
                cellType: CellType.BOOLEAN,
            };
        case "description":
            return {
                field: column.field,
                label: column.label,
                cellType: CellType.STRING,
            };
        default: {
            const exhaustiveField: never = column.field;
            return exhaustiveField;
        }
    }
}

export function buildEliminatingGridColumns({
    visibleColumns,
    ...params
}: BuildColumnsParams): ColumnDef[] {
    return visibleColumns.map((column) => buildColumn({ column, ...params }));
}
